import { clsx } from 'clsx';
import { CSSProperties, PropsWithChildren } from 'react';
import { Modal as AriaModal, Dialog, ModalOverlay } from 'react-aria-components';
import CircleSpinner from '~/components/data-display/CircleSpinner/CircleSpinner';
import useDataTestId from '~/hooks/useDataTestId';
import { useIsTopModal } from '~/modals/ModalTopStackContext';
import styles from './Modal.module.scss';

interface ModalProps {
  action?: 'confirm' | 'destructive';
  'aria-label'?: string;
  'aria-labelledby'?: string;
  className?: string;
  dialogClassName?: string;
  isAlert?: boolean;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  maxWidth?: CSSProperties['maxWidth'];
  fullWidth?: boolean;
  dataTestId?: string;
  forceForeground?: boolean;
  style?: CSSProperties;
  isLoading?: boolean;
  isDismissable?: boolean;
  /**
   * Enables responsive styles for the modal.
   *
   * Note: This could potentially be set to true by default since all modals
   * should benefit from responsive behavior, but it's kept as an opt-in prop
   * for backward compatibility and to prevent breaking existing modals.
   */
  responsive?: boolean;
}

const Modal: React.FC<PropsWithChildren<ModalProps>> = (props) => {
  const { maxWidth = '500px', dataTestId, style, isLoading, dialogClassName } = props;
  const dt = useDataTestId(dataTestId);
  const isTop = useIsTopModal();

  return (
    <ModalOverlay
      data-testid={dt('overlay')}
      className={clsx(styles.modalOverlay, {
        [styles.responsive!]: props.responsive,
        [styles.forceForeground!]: props.forceForeground,
        [styles.fullWidth!]: props.fullWidth,
        [styles.hidden!]: !isTop,
      })}
      isDismissable={props.isDismissable ?? true}
      isOpen={props.isOpen}
      onOpenChange={props.onOpenChange}
    >
      <AriaModal data-testid={dt('ariaModal')} className={clsx(styles.ariaModal, props.className)}>
        <Dialog
          aria-label={props['aria-label']}
          aria-labelledby={props['aria-labelledby']}
          data-testid={dt()}
          role={props.isAlert ? 'alertdialog' : undefined}
          className={clsx(styles.dialog, dialogClassName)}
          data-action={props.action}
          style={{ ...style, maxWidth }}
        >
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <CircleSpinner />
            </div>
          ) : (
            props.children
          )}
        </Dialog>
      </AriaModal>
    </ModalOverlay>
  );
};

export default Modal;
